import React from "react";
import { Link } from "react-router-dom";
import { Column } from "./Layout";
import { Signature } from "./Signature";
import { FadeIn } from "./FadeIn";
import { OutlineLink } from "./OutlineLink";
import { SampleDownloadButton } from "./SampleDownloadButton";
import { buildLockupSvg, STACKED_COLOPHON_WIDTH } from "../admin/logoAssets";

const PHONE = "+27 79 281 8435";
const PHONE_HREF = "https://wa.me/27792818435";

const PORTRAIT =
  "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/9d4b613651471f94371bd47c34f96eba83907751c85a4a7669b50e8ddea54700.png";

const BODY = [
  "Samson Josiah Maluleke was born on 3 August 1941 at Valdezia, in what was then the Northern Transvaal, the second of the five children of Mhlava Wilson Maluleke and Tsakani Annah Maluleke, née Baloyi. He was named for his grandfather, Josiah Maluleke, an evangelist and a man of the Book, and he grew up between two houses of instruction: that of his paternal grandparents, Josiah and Mihloti Maluleke, and that of his mother's parents, Daniel and Fokisa Baloyi, in whose kraal he herded cattle and learned, as he would later tell his pupils, that a thing counted every evening is a thing that does not get lost. His siblings were Yingwani, Sasavona, Mkhacani and Tinyiko, and to the end of his life he could tell you what each of them was doing on any given Sunday.",
  "He began school at the Valdezia Mission School in 1948, a year ahead of custom, by a method the family still recounts: sent along to accompany his elder brother Yingwani on the first morning, he sat down at a desk and declined to rise, and the teacher, seeing that the matter was already settled, wrote his name in the register. It was the first register ever to hold his name. He would go on to keep registers of his own for thirty-one years. He completed his schooling at Lemana, near Elim, and stayed on to train as a teacher, as so many of that place and generation did, qualifying in 1962.",
  "In 1965 he followed the work to Pretoria and joined the staff of a primary school in Mamelodi, and in 1972 he was appointed principal of Rhulani Primary School, a post he held for thirty-one years until his retirement in 2003. It was in Mamelodi, in 1967, at a wedding where he had been pressed into service as master of ceremonies, that he met Florah Ntsakisi Chauke, a young nurse who would later say that she married him because he was the only man she had ever met who ran a wedding to time. They married in 1973, and were husband and wife for fifty-three years. Their marriage was blessed with four children, Hasani, Tintswalo, Nyeleti and Vukosi, and in time with eleven grandchildren, set down here as he always recited them, in order of birth: Ntsako, Mikateko, Rivoningo, Vukona, Tsakani (named for his mother), Hlulani, Ntwanano, Vutomi, Nyiko, Akani and Rifumo.",
  "Retirement changed the school but never the timetable. His mornings kept an order his old staff would have recognised: the gate unlocked at six, tea with Florah at the kitchen table, the newspaper read with a red pen in hand, corrections in the margin that no editor would ever see. And every evening he counted his family, the way the kraal had taught him and thirty-one years of registers had confirmed: each of the four children, each of the eleven grandchildren, scattered from Mamelodi to Manchester, accounted for by call or by message before he slept, because a thing counted every evening is a thing that does not get lost.",
  "His voice notes ran long, like his assemblies, and his typed messages he signed, From S.J. Maluleke, as though the family might otherwise wonder who had written to them from his number. Every message he sent ended the same way those assemblies had, with the question a generation of Mamelodi can finish without him: are we together? Video calls he conducted at close range, so that a granddaughter in Manchester grew up knowing the kitchen ceiling and the top of her kokwana's head, and would not have had it any other way. The duty roster for the family's December gathering was already on the kitchen door in his handwriting, every task assigned. The family will keep to it exactly as he set it down.",
  "Of his character, let this be the record. In the three days since his passing, the people who loved him allowed a stranger to sit in his house and listen, and one thing returned so often that it stopped being coincidence and became the finding of his life: nearly everyone carried a sentence he had once said to them, quietly, at the exact moment it was needed, and nearly everyone believed, until this week, that their sentence had been said to them alone. To a boy kept behind after class, certain he was about to be punished: you are not a problem, you are a project. To a young teacher writing her resignation letter: stay until you have seen one class through, then decide. Different sentences, different decades, the same hand. He could see the exact size of a person before they could see it themselves, and he would say so, once, and then let the sentence do its work for forty years. His children will add, with more laughter than grief, that they shared their father with every child in Mamelodi, and that some evenings the best of his patience was given away by four o'clock. They say it without bitterness, and it stays in this obituary because he would not have wanted a polished man buried this morning. He marked honestly, and he would expect to be marked honestly.",
  "He passed away peacefully at his home on the morning of 21 June 2026, with Florah beside him, after a short illness that he carried the way he carried everything, quietly and without complaint.",
  "His glasses are folded on the newspaper where he left them, beside the red pen.",
  "The last bell has rung, Principal. The classrooms are quiet. Everything you marked is out in the world now, and it is holding. It's all together.",
  "Famba kahle, Maluleke.",
];

export const FromTheBookletSpecimen = ({ standalone = false }) => {
  return (
    <>
      {/* FRAMING COPY */}
      <section className="pt-4 pb-14 md:pb-16">
        <Column className="text-center">
          <Signature label="From the Booklet" testid="from-the-booklet-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]" data-testid="from-the-booklet-framing">
            Every commission is confidential, without exception. No family&rsquo;s
            farewell appears on this website, and none ever will. So that you may still
            see how the work reads, the tribute below was written for an invented man.
            Samson Maluleke does not exist, and no one mourns him. The method does exist:
            seventy-two hours of listening, and then the writing. What follows is set as
            it would appear in the funeral booklet, prepared for public delivery.
          </FadeIn>
        </Column>
      </section>

      {/* THE BOOKLET LEAF */}
      <section className="pb-16 md:pb-20">
        <div className="mx-auto max-w-[560px] px-6">
          <FadeIn>
            <div
              className="border border-[#7A2E2E] bg-[#FAF8F2] px-8 py-14 sm:px-12 sm:py-16"
              style={{ boxShadow: "0 10px 28px rgba(28,26,23,0.09)" }}
              data-testid="from-the-booklet-leaf"
              role="img"
              aria-label="A typeset page from a Final Tribute specimen funeral booklet"
            >
              {/* Header block */}
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#6E6A5E]">
                  In Loving Memory Of
                </p>
                <h2 className="mt-5 text-[26px] md:text-[30px] italic leading-[1.3] text-[#1C1A17]">
                  Samson Josiah Maluleke
                </h2>
                <p className="mt-4 text-[13px] text-[#6E6A5E]">
                  3 August 1941 · 21 June 2026
                </p>
                <hr className="mx-auto mt-6 border-0" style={{ width: "56px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
                <p className="mt-6 text-[14px] italic leading-[1.6] text-[#6E6A5E]">
                  The obituary, prepared with the family, for public delivery
                </p>
              </div>

              {/* Portrait */}
              <figure className="mt-10 mx-auto max-w-[280px]" data-testid="from-the-booklet-portrait">
                <div className="border border-[#A8A296]">
                  <div className="bg-white p-2">
                    <img
                      src={PORTRAIT}
                      alt="A demonstration obituary portrait: a dignified older man in a jacket and tie, looking warmly to camera against a plain background."
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
                <figcaption className="mt-5 text-center">
                  <p className="text-[16px] italic text-[#1C1A17]">&ldquo;Are we together?&rdquo;</p>
                </figcaption>
              </figure>

              {/* Body: book setting, first-line indents, no blank lines */}
              <div className="mt-10 text-[17px] leading-[1.75] text-[#1C1A17]">
                {BODY.map((para, i) => (
                  <p key={i} className={i === 0 ? "" : "[text-indent:1.6em]"}>
                    {para}
                  </p>
                ))}
              </div>

              {/* Press colophon: the stacked Raven's Quill lockup (shared token) */}
              <div
                className="mt-14 mx-auto"
                style={{ width: STACKED_COLOPHON_WIDTH }}
                data-testid="logo-lockup"
                dangerouslySetInnerHTML={{ __html: buildLockupSvg("ink") }}
              />
            </div>
          </FadeIn>
          <FadeIn className="mt-10 flex justify-center">
            <SampleDownloadButton
              targetTestId="from-the-booklet-leaf"
              filename="the-ravens-quill-final-tribute-sample.pdf"
              label="Download This Tribute as PDF"
              testid="from-the-booklet-download"
            />
          </FadeIn>
        </div>
      </section>

      {/* CLOSING COPY */}
      <section className="pb-24 md:pb-32">
        <Column className="text-center">
          <FadeIn>
            <p className="text-[19px] md:text-[21px] italic leading-[1.6] text-[#1C1A17]" data-testid="from-the-booklet-close">
              Written in seventy-two hours. Carried for a lifetime.
            </p>
          </FadeIn>
          <FadeIn className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center">
            {/* Mobile: tap-to-call */}
            <span className="sm:hidden">
              <OutlineLink as="anchor" to={PHONE_HREF} target="_blank" rel="noopener noreferrer" testid="from-the-booklet-cta-mobile">
                Enquire About The Final Tribute
              </OutlineLink>
            </span>
            {/* Desktop: anchor to To Begin on the Tribute page, or WhatsApp on standalone */}
            <span className="hidden sm:inline-flex">
              <OutlineLink
                as="anchor"
                to={standalone ? PHONE_HREF : "#to-begin"}
                target={standalone ? "_blank" : undefined}
                rel={standalone ? "noopener noreferrer" : undefined}
                testid="from-the-booklet-cta"
              >
                Enquire About The Final Tribute
              </OutlineLink>
            </span>
            <OutlineLink to="/the-method" variant="oxblood" testid="from-the-booklet-life-study">
              Read About The Life Study
            </OutlineLink>
          </FadeIn>

          {!standalone && (
            <FadeIn>
              <p className="mt-10 text-[15px] text-[#6E6A5E]">
                <Link
                  to="/from-the-booklet"
                  data-testid="from-the-booklet-share-link"
                  className="italic text-[#6E6A5E] underline underline-offset-4 hover:text-[#7A2E2E] transition-colors"
                >
                  Share this specimen
                </Link>
              </p>
            </FadeIn>
          )}
        </Column>
      </section>
    </>
  );
};
